<script setup>
import { ref, watch, onUnmounted, nextTick, computed } from 'vue';
import { useGeolocation } from '@/composables/useGeolocation.js';
import { useLocation } from '@/composables/useLocation.js';
import { debounce } from 'lodash';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('MapPickerModal');

const props = defineProps({
  show: Boolean,
  initialCenter: { type: Object, default: null },   // { lat, lng } from Hero's selected location
  initialAddress: { type: String, default: '' },     // Hero's locationInput text
});
const emit  = defineEmits(['close', 'confirm']);
const { requestLocation } = useGeolocation();
const { userLat, userLng, locationReady, initLocation } = useLocation();

// ── Refs ───────────────────────────────────────────────────────────────────
const mapEl         = ref(null);
const searchInputEl = ref(null);
const mapAddress    = ref('');
const searchQuery   = ref('');
const isReady       = ref(false);
const isLocating    = ref(false);   // spinner while map loads
const gpsError      = ref('');      // only set when user explicitly clicks GPS
const isRetryingGPS = ref(false);
const suggestions   = ref([]);      // live autocomplete results
const showSuggestions = ref(false);
const isSearching   = ref(false);   // spinner in search while fetching suggestions
let _suppressSync   = false;

let map              = null;
let marker           = null;
let geocoder         = null;
let circle           = null;
let _sessionToken    = null;       // AutocompleteSuggestion session token

// ── Wait for Google ────────────────────────────────────────────────────────
const waitForGoogle = () => new Promise(resolve => {
  const t = setInterval(() => {
    if (window.google?.maps?.places?.AutocompleteSuggestion) { clearInterval(t); resolve(); }
  }, 80);
});

// ── Reverse geocode ────────────────────────────────────────────────────────
const reverseGeocode = (lat, lng) => new Promise(resolve => {
  geocoder.geocode({ location: { lat, lng } }, (results, status) => {
    if (status !== 'OK' || !results[0]) return resolve(null);
    const ac  = results[0].address_components;
    const get = type => ac.find(c => c.types.includes(type))?.long_name || '';

    // Street: standard number+route, or fall back to landmark/neighbourhood/plus_code area
    const streetStandard = [get('street_number'), get('route')].filter(Boolean).join(' ');
    const streetFallback =
      get('premise') ||
      get('sublocality_level_1') ||
      get('sublocality') ||
      get('neighborhood') ||
      get('plus_code') ||
      '';
    const street_address = streetStandard || streetFallback;

    // City: locality (town/city), then named area (postal_town), then county/district
    const city =
      get('locality') ||
      get('postal_town') ||
      get('administrative_area_level_2') ||
      get('administrative_area_level_1') ||
      '';

    // State: administrative_area_level_1, or level_2 if level_1 is huge (e.g. country-level)
    const state = get('administrative_area_level_1') || get('administrative_area_level_2') || '';

    // Postal code with prefix fallback for areas without full codes
    const zip_code = get('postal_code') || get('postal_code_prefix') || '';

    resolve({
      formatted:      results[0].formatted_address,
      street_address,
      city,
      state,
      zip_code,
      country: get('country'),
      lat, lng,
    });
  });
});

// ── Move marker + sync search input ───────────────────────────────────────
const moveMarkerTo = async (lat, lng, label = null) => {
  const pos = { lat, lng };
  marker.setPosition(pos);
  map.panTo(pos);
  map.setZoom(17);
  if (circle) circle.setCenter(pos);
  mapAddress.value = 'Locating…';
  const r = await reverseGeocode(lat, lng);
  mapAddress.value = r?.formatted || '';
  _suppressSync = true;
  searchQuery.value = label || mapAddress.value;
  showSuggestions.value = false;
  await nextTick();
  _suppressSync = false;
};

// ── Live suggestions as user types (new AutocompleteSuggestion API) ───────
const fetchSuggestions = debounce(async (query) => {
  if (!query.trim() || _suppressSync) {
    suggestions.value = [];
    isSearching.value = false;
    return;
  }
  if (!window.google?.maps?.places?.AutocompleteSuggestion) return;
  isSearching.value = true;
  
  // Create a new session token if we don't have one
  if (!_sessionToken) {
    _sessionToken = new window.google.maps.places.AutocompleteSessionToken();
  }
  
  const request = {
    input: query,
    sessionToken: _sessionToken,
    ...(map ? { locationBias: map.getBounds() } : {}),
  };
  
  try {
    const { suggestions: results } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    
    isSearching.value = false;
    if (results?.length) {
      suggestions.value = results
        .filter(s => s.placePrediction)
        .map(s => ({
          placeId:     s.placePrediction.placeId,
          main:        s.placePrediction.mainText?.toString() || '',
          secondary:   s.placePrediction.secondaryText?.toString() || '',
          description: s.placePrediction.text?.toString() || '',
          _prediction: s.placePrediction,
        }));
      showSuggestions.value = true;
    } else {
      suggestions.value = [];
      showSuggestions.value = false;
    }
  } catch (error) {
    isSearching.value = false;
    suggestions.value = [];
    showSuggestions.value = false;
    logger.error('Autocomplete suggestion error', error);
  }
}, 250);

const onSearchInput = () => {
  if (_suppressSync) return;
  showSuggestions.value = false;
  fetchSuggestions(searchQuery.value);
};

// ── Select a suggestion ────────────────────────────────────────────────────
const selectSuggestion = async (suggestion) => {
  showSuggestions.value = false;
  suggestions.value = [];
  searchQuery.value = suggestion.description;

  try {
    // Use toPlace() from prediction if available, otherwise create from ID
    const place = suggestion._prediction
      ? suggestion._prediction.toPlace()
      : new window.google.maps.places.Place({ id: suggestion.placeId });
    
    await place.fetchFields({
      fields: ['displayName', 'location', 'formattedAddress', 'addressComponents'],
    });
    
    if (!place.location) return;
    
    const lat   = place.location.lat();
    const lng   = place.location.lng();
    const label = place.displayName && place.displayName !== place.formattedAddress
      ? `${place.displayName}, ${place.formattedAddress || ''}`.replace(/,\s*$/, '')
      : place.formattedAddress || '';

    if (place.viewport) {
      map.fitBounds(place.viewport);
      marker.setPosition({ lat, lng });
      if (circle) circle.setCenter({ lat, lng });
      mapAddress.value = 'Locating…';
      const r = await reverseGeocode(lat, lng);
      mapAddress.value = r?.formatted || label;
      _suppressSync = true;
      searchQuery.value = label;
      await nextTick();
      _suppressSync = false;
    } else {
      moveMarkerTo(lat, lng, label);
    }
    
    // Reset session token after place is selected
    _sessionToken = null;
  } catch (error) {
    logger.error('Error fetching place details', error);
  }
};

const clearSearch = () => {
  searchQuery.value = '';
  suggestions.value = [];
  showSuggestions.value = false;
  searchInputEl.value?.focus();
};

// ── GPS — only called when user explicitly clicks the button ───────────────
const useMyLocation = async () => {
  gpsError.value   = '';
  isRetryingGPS.value = true;
  const result = await requestLocation();
  isRetryingGPS.value = false;
  if (result.coords) {
    moveMarkerTo(result.coords.lat, result.coords.lng);
  } else {
    // Only show error when user explicitly requested GPS
    gpsError.value = result.denied
      ? 'Location access is blocked. Enable it in your browser settings (🔒 lock icon → Location → Allow), then try again.'
      : 'We couldn\'t determine your current location. Try searching for your area instead.';
  }
};

// ── Build map ──────────────────────────────────────────────────────────────
const buildMap = async (center) => {
  if (!mapEl.value) return;

  map = new google.maps.Map(mapEl.value, {
    center,
    zoom: 17,
    mapTypeControl:    false,
    streetViewControl: false,
    fullscreenControl: false,
    clickableIcons:    false,
    gestureHandling:   'greedy',
    zoomControl: true,
    zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
  });

  geocoder         = new google.maps.Geocoder();

  // Draggable orange pin
  marker = new google.maps.Marker({
    position:  center,
    map,
    draggable: true,
    animation: google.maps.Animation.DROP,
    icon: {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
          <defs>
            <filter id="s" x="-30%" y="-20%" width="160%" height="160%">
              <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <g filter="url(#s)">
            <path d="M20 2C11.163 2 4 9.163 4 18c0 12.5 16 30 16 30s16-17.5 16-30C36 9.163 28.837 2 20 2z"
              fill="#ff6f00" stroke="#fff" stroke-width="2"/>
            <circle cx="20" cy="18" r="6.5" fill="#fff"/>
            <circle cx="20" cy="18" r="3.5" fill="#ff6f00"/>
          </g>
        </svg>
      `),
      scaledSize: new google.maps.Size(40, 52),
      anchor:     new google.maps.Point(20, 52),
    },
    zIndex: 2,
  });

  // Blue GPS dot
  new google.maps.Marker({
    position: center, map, clickable: false,
    icon: {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="11" fill="rgba(66,133,244,0.15)" stroke="rgba(66,133,244,0.4)" stroke-width="1"/>
          <circle cx="12" cy="12" r="6" fill="#4285F4" stroke="#fff" stroke-width="2.5"/>
        </svg>
      `),
      scaledSize: new google.maps.Size(24, 24),
      anchor:     new google.maps.Point(12, 12),
    },
    zIndex: 1,
  });

  circle = new google.maps.Circle({
    map, center,
    radius: 35, fillColor: '#ff6f00', fillOpacity: 0.1,
    strokeColor: '#ff6f00', strokeOpacity: 0.35, strokeWeight: 1,
  });

  marker.addListener('dragend', () => {
    const p = marker.getPosition();
    moveMarkerTo(p.lat(), p.lng());
  });

  map.addListener('click', e => {
    moveMarkerTo(e.latLng.lat(), e.latLng.lng());
  });

  mapAddress.value = 'Locating…';
  const r = await reverseGeocode(center.lat, center.lng);
  mapAddress.value = r?.formatted || '';
  _suppressSync = true;
  searchQuery.value = mapAddress.value;
  await nextTick();
  _suppressSync = false;
  isReady.value = true;
};

// ── Cleanup ────────────────────────────────────────────────────────────────
const cleanup = () => {
  if (marker) { google.maps.event.clearInstanceListeners(marker); marker = null; }
  if (map)    { google.maps.event.clearInstanceListeners(map);    map    = null; }
  if (circle) { circle.setMap(null);                              circle = null; }
  mapAddress.value    = '';
  searchQuery.value   = '';
  suggestions.value   = [];
  showSuggestions.value = false;
  isReady.value       = false;
  isLocating.value    = false;
  gpsError.value      = '';
};

// ── Watch show ─────────────────────────────────────────────────────────────
watch(() => props.show, async (val) => {
  if (!val) { cleanup(); return; }

  gpsError.value  = '';
  isLocating.value = true;
  await waitForGoogle();

  // Wait for map DOM element
  await new Promise(resolve => {
    const poll = () => mapEl.value ? resolve() : setTimeout(poll, 30);
    nextTick(poll);
  });

  let center = null;

  // 1. Use initialCenter if provided (user already selected a location in Hero)
  if (props.initialCenter?.lat && props.initialCenter?.lng) {
    center = { lat: Number(props.initialCenter.lat), lng: Number(props.initialCenter.lng) };
  }

  // 2. Try GPS
  if (!center) {
    const gpsResult = await requestLocation();
    if (gpsResult.coords) center = gpsResult.coords;
  }

  // 3. IP-based fallback (from useLocation composable)
  if (!center) {
    await initLocation();
    if (locationReady.value && userLat.value && userLng.value) {
      center = { lat: userLat.value, lng: userLng.value };
    }
  }

  // 4. Hardcoded fallback
  if (!center) center = { lat: -1.286389, lng: 36.817223 };

  isLocating.value = false;
  await buildMap(center);

  // Pre-populate search query with Hero's address if provided
  if (props.initialAddress) {
    _suppressSync = true;
    searchQuery.value = props.initialAddress;
    await nextTick();
    _suppressSync = false;
  }
});

// ── Confirm ────────────────────────────────────────────────────────────────
const confirm = async () => {
  if (!marker) return;
  const p = marker.getPosition();
  const r = await reverseGeocode(p.lat(), p.lng());
  if (r) emit('confirm', r);
};

onUnmounted(cleanup);
</script>

<template>
  <teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 flex items-center justify-center bg-black/60 p-3 sm:p-5"
      style="z-index: 99999;"
      @click.self="$emit('close')"
    >
      <div
        class="bg-theme-card rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col"
        style="height: 92vh; max-height: 700px;"
      >

        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-theme-border shrink-0">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 text-primary-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
            <div>
              <h3 class="text-sm font-bold text-theme-text">Pick your location</h3>
              <p class="text-xs text-theme-muted">Search, drag the pin, or tap the map</p>
            </div>
          </div>
          <button @click="$emit('close')"
            class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-theme-bg2 text-theme-muted hover:text-theme-text2 transition">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Search bar -->
        <div class="px-3 py-2.5 border-b border-theme-border shrink-0 relative">
          <div class="flex items-center gap-2 bg-theme-bg2 border border-theme-border rounded-xl px-3 py-2
                      focus-within:border-primary-600 focus-within:ring-2 focus-within:ring-primary-600/20 transition">
            <!-- Search icon / spinner -->
            <svg v-if="!isSearching" class="w-4 h-4 text-theme-muted shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <svg v-else class="w-4 h-4 text-primary-600 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>

            <input
              ref="searchInputEl"
              v-model="searchQuery"
              @input="onSearchInput"
              @focus="searchQuery && suggestions.length && (showSuggestions = true)"
              @keydown.escape="showSuggestions = false"
              @keydown.enter.prevent="suggestions[0] && selectSuggestion(suggestions[0])"
              type="text"
              placeholder="Search for a place, street, landmark…"
              class="flex-1 bg-transparent text-sm text-theme-text placeholder-theme-muted focus:outline-none"
              autocomplete="off"
            />

            <!-- GPS button -->
            <button @click="useMyLocation" :disabled="isRetryingGPS"
              class="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-primary-50 text-theme-muted hover:text-primary-600 transition disabled:opacity-50"
              title="Use my current location">
              <svg v-if="!isRetryingGPS" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                <circle cx="12" cy="12" r="8" stroke-dasharray="2 2"/>
              </svg>
              <svg v-else class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
              </svg>
            </button>

            <!-- Clear -->
            <button v-if="searchQuery" @click="clearSearch"
              class="shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-neutral-200 hover:bg-neutral-300 text-theme-muted transition">
              <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Live suggestions dropdown -->
          <transition name="suggestions">
            <div v-if="showSuggestions && suggestions.length"
              class="absolute left-3 right-3 top-full mt-1 bg-theme-card rounded-xl shadow-xl border border-theme-border overflow-hidden"
              style="z-index: 10;">
              <ul>
                <li v-for="(s, i) in suggestions" :key="s.placeId"
                  @mousedown.prevent="selectSuggestion(s)"
                  class="flex items-start gap-3 px-4 py-3 hover:bg-theme-bg2 cursor-pointer transition border-b border-theme-border last:border-0">
                  <!-- Place icon -->
                  <div class="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center shrink-0 mt-0.5">
                    <svg class="w-3.5 h-3.5 text-primary-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-theme-text truncate">{{ s.main }}</p>
                    <p class="text-xs text-theme-muted truncate mt-0.5">{{ s.secondary }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </transition>
        </div>

        <!-- GPS error — only shown when user explicitly clicked GPS button -->
        <transition name="slide-down">
          <div v-if="gpsError"
            class="mx-3 mt-2 shrink-0 bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-start gap-3">
            <svg class="w-4 h-4 text-red-400 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
            </svg>
            <p class="text-xs text-red-600 flex-1 leading-relaxed">{{ gpsError }}</p>
            <button @click="gpsError = ''" class="text-red-300 hover:text-red-500 shrink-0">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </transition>

        <!-- Map -->
        <div class="relative flex-1 min-h-0" style="height: 0;">
          <div ref="mapEl" style="width:100%; height:100%;"></div>

          <!-- Loading overlay -->
          <div v-if="!isReady"
            class="absolute inset-0 bg-theme-card flex flex-col items-center justify-center gap-4">
            <div class="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center">
              <svg class="w-7 h-7 text-primary-600 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
              </svg>
            </div>
            <div class="text-center">
              <p class="text-sm font-semibold text-theme-text">Loading map…</p>
              <p class="text-xs text-theme-muted mt-1">Allow location access when your browser asks</p>
            </div>
          </div>

          <!-- Hint pill -->
          <div v-if="isReady"
            class="absolute top-3 left-1/2 -translate-x-1/2 bg-neutral-900/70 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none flex items-center gap-1.5 backdrop-blur-sm whitespace-nowrap">
            <svg class="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            </svg>
            Drag the pin or tap anywhere to move it
          </div>
        </div>

        <!-- Footer -->
        <div class="px-4 py-3 border-t border-theme-border bg-theme-bg2 rounded-b-2xl shrink-0">
          <div class="flex items-center gap-2 mb-3 min-h-[20px]">
            <svg class="w-4 h-4 text-primary-600 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <p class="text-sm text-theme-text leading-snug truncate">
              <span v-if="mapAddress === 'Locating…'" class="text-theme-muted italic flex items-center gap-1">
                <svg class="w-3.5 h-3.5 animate-spin shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Locating…
              </span>
              <span v-else-if="mapAddress">{{ mapAddress }}</span>
              <span v-else class="text-theme-muted text-xs">Search or move the pin to set your location</span>
            </p>
          </div>
          <div class="flex gap-2.5">
            <button @click="$emit('close')"
              class="flex-1 py-2.5 rounded-xl border border-theme-border text-theme-text2 text-sm font-medium hover:bg-theme-bg2 transition">
              Cancel
            </button>
            <button @click="confirm"
              :disabled="!mapAddress || mapAddress === 'Locating…'"
              class="flex-1 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-400 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              Confirm location
            </button>
          </div>
        </div>

      </div>
    </div>
  </teleport>
</template>

<style scoped>
.suggestions-enter-active, .suggestions-leave-active { transition: all 0.15s ease; }
.suggestions-enter-from, .suggestions-leave-to { opacity: 0; transform: translateY(-6px); }
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.2s ease; }
.slide-down-enter-from, .slide-down-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
